/**
 * BusinessCRMFields.parts.js
 *
 * Sub-components extracted from BusinessCRMFields.js so the parent stays small
 * and each piece can be tested/reused in isolation.
 *
 * All parts are framework-agnostic: they receive props and call back. Side
 * effects (network, toasts) live in the parent.
 */
import React from "react";
import { Plus, Trash2, X, Loader2 } from "lucide-react";
import { InlineText } from "./InlineEdit";

export const FIELD_TYPES = [
  { value: "text", label: "Texto" },
  { value: "email", label: "Email" },
  { value: "number", label: "Número" },
  { value: "date", label: "Fecha" },
  { value: "url", label: "URL" },
];

// ─── Shared section wrapper ─────────────────────────────────────────────────
export function Section({ title, icon, action, children }) {
  return (
    <div className="bg-[#0f0f0f] border border-white/[0.06] rounded-xl p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 text-[11px] font-bold text-gray-400 uppercase tracking-wide">
          {icon} {title}
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

// ─── Tags section ──────────────────────────────────────────────────────────
export function TagsSection({ tags, tagInput, setTagInput, onAdd, onRemove }) {
  return (
    <>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {tags.length === 0 && <span className="text-xs text-gray-500 italic">Sin etiquetas</span>}
        {tags.map((t) => (
          <span
            key={t}
            data-testid={`tag-${t}`}
            className="inline-flex items-center gap-1 text-[11px] bg-white/[0.06] text-gray-200 rounded-full px-2 py-0.5 border border-white/[0.08]"
          >
            {t}
            <button
              data-testid={`tag-${t}-remove`}
              onClick={() => onRemove(t)}
              className="text-gray-500 hover:text-red-400"
            ><X size={10} /></button>
          </span>
        ))}
      </div>
      <div className="flex gap-1.5">
        <input
          data-testid="tag-input"
          value={tagInput}
          onChange={(e) => setTagInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onAdd(); } }}
          placeholder="Nueva etiqueta…"
          className="flex-1 bg-white/[0.04] border border-white/[0.08] rounded-lg px-2 py-1 text-xs text-gray-200 focus:outline-none focus:border-white/[0.15]"
        />
        <button
          data-testid="tag-add-btn"
          onClick={onAdd}
          className="text-xs font-semibold text-gray-400 hover:text-[#FF4F00] px-2"
        >+ Agregar</button>
      </div>
    </>
  );
}

// ─── Follow-up section ─────────────────────────────────────────────────────
export function FollowUpSection({ date, notes, onDateChange, onNotesChange }) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-2 items-start">
      <input
        data-testid="followup-date"
        type="date"
        value={(date || "").slice(0, 10)}
        onChange={(e) => onDateChange(e.target.value)}
        className="bg-white/[0.04] border border-white/[0.08] rounded-lg px-2 py-1 text-xs text-gray-200 focus:outline-none focus:border-white/[0.15]"
      />
      <input
        data-testid="followup-notes"
        value={notes || ""}
        onChange={(e) => onNotesChange(e.target.value)}
        onBlur={(e) => onNotesChange(e.target.value)}
        placeholder="Qué hacer en el próximo contacto…"
        className="bg-white/[0.04] border border-white/[0.08] rounded-lg px-2 py-1 text-xs text-gray-200 focus:outline-none focus:border-white/[0.15]"
      />
    </div>
  );
}

// ─── Notes timeline section ────────────────────────────────────────────────
export function NotesTimeline({ notes, noteInput, setNoteInput, onAdd, onRemove }) {
  return (
    <>
      {notes.length === 0 && (
        <p className="text-xs text-gray-500 italic mb-2">Sin notas todavía.</p>
      )}
      <div className="space-y-1.5 mb-2 max-h-48 overflow-y-auto pr-1">
        {notes.slice().reverse().map((n, i) => (
          <NoteCard key={n.id || `n-${i}`} note={n} index={i} onRemove={() => onRemove(n.id || n.text)} />
        ))}
      </div>
      <div className="flex gap-1.5">
        <input
          data-testid="note-input"
          value={noteInput}
          onChange={(e) => setNoteInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); onAdd(); } }}
          placeholder="Nueva nota (Enter para guardar)…"
          className="flex-1 bg-white/[0.04] border border-white/[0.08] rounded-lg px-2 py-1 text-xs text-gray-200 focus:outline-none focus:border-white/[0.15]"
        />
        <button
          data-testid="note-add-btn"
          onClick={onAdd}
          className="text-xs font-semibold text-gray-400 hover:text-[#FF4F00] px-2"
        >+ Agregar</button>
      </div>
    </>
  );
}

function NoteCard({ note, index, onRemove }) {
  return (
    <div className="bg-white/[0.04] border border-white/[0.06] rounded-lg p-2 flex justify-between gap-2 group">
      <div className="flex-1 min-w-0">
        <p className="text-xs text-gray-200 whitespace-pre-wrap break-words">{note.text}</p>
        {note.created_at && (
          <p className="text-[10px] text-gray-500 mt-0.5">{new Date(note.created_at).toLocaleString()}</p>
        )}
      </div>
      <button
        data-testid={`note-${note.id || index}-remove`}
        onClick={onRemove}
        className="opacity-0 group-hover:opacity-100 text-gray-500 hover:text-red-400 transition-opacity"
      ><Trash2 size={11} /></button>
    </div>
  );
}

// ─── Custom fields section ─────────────────────────────────────────────────
export function CustomFieldsSection({
  schema, loading, business,
  addingField, setAddingField, newField, setNewField,
  onCreateSchema, onDeleteSchema, onSaveValue, onClearValue,
}) {
  return (
    <>
      {addingField && (
        <NewFieldForm
          newField={newField}
          setNewField={setNewField}
          onSubmit={onCreateSchema}
          onCancel={() => { setAddingField(false); setNewField({ label: "", field_id: "", type: "text" }); }}
        />
      )}
      {loading ? (
        <div className="flex items-center gap-1.5 text-xs text-gray-500">
          <Loader2 size={11} className="animate-spin" /> Cargando…
        </div>
      ) : schema.length === 0 ? (
        <p className="text-xs text-gray-500 italic">
          Sin campos personalizados. Creá uno y usalo en prompts como{" "}
          <code className="bg-white/[0.06] text-gray-300 px-1 rounded">{`{{campo.id}}`}</code>.
        </p>
      ) : (
        <div className="space-y-1.5">
          {schema.map((s) => (
            <CustomFieldRow
              key={s.id}
              schema={s}
              value={business.custom?.[s.field_id] ?? ""}
              onSave={(v) => onSaveValue(s.field_id, v)}
              onClear={() => onClearValue(s.field_id)}
              onDeleteSchema={() => onDeleteSchema(s)}
            />
          ))}
        </div>
      )}
    </>
  );
}

function NewFieldForm({ newField, setNewField, onSubmit, onCancel }) {
  return (
    <div className="bg-white/[0.04] border border-white/[0.08] rounded-lg p-2 mb-2 space-y-1.5">
      <input
        data-testid="new-field-label-input"
        placeholder="Nombre (ej: LinkedIn)"
        value={newField.label}
        onChange={(e) => setNewField((f) => ({ ...f, label: e.target.value }))}
        className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-2 py-1 text-xs text-gray-200 focus:outline-none focus:border-white/[0.15]"
        autoFocus
      />
      <div className="flex gap-1.5">
        <input
          data-testid="new-field-id-input"
          placeholder="ID técnico (opcional)"
          value={newField.field_id}
          onChange={(e) => setNewField((f) => ({ ...f, field_id: e.target.value }))}
          className="flex-1 bg-white/[0.04] border border-white/[0.08] rounded-lg px-2 py-1 text-[11px] text-gray-200 focus:outline-none focus:border-white/[0.15]"
        />
        <select
          data-testid="new-field-type-select"
          value={newField.type}
          onChange={(e) => setNewField((f) => ({ ...f, type: e.target.value }))}
          className="bg-white/[0.04] border border-white/[0.08] rounded-lg px-2 py-1 text-[11px] text-gray-200"
        >
          {FIELD_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </div>
      <div className="flex justify-end gap-1.5">
        <button onClick={onCancel} className="text-xs text-gray-500 hover:text-gray-300 px-2 py-1">
          Cancelar
        </button>
        <button
          data-testid="new-field-save-btn"
          onClick={onSubmit}
          className="text-xs bg-[#FF4F00] text-white px-2.5 py-1 rounded-lg font-semibold hover:bg-[#e64700]"
        >Crear</button>
      </div>
    </div>
  );
}

function CustomFieldRow({ schema, value, onSave, onClear, onDeleteSchema }) {
  return (
    <div className="flex items-start gap-2 group">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
          <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wide">
            {schema.label}
          </span>
          <code className="text-[10px] text-gray-500 bg-white/[0.04] px-1 rounded">
            {`{{campo.${schema.field_id}}}`}
          </code>
        </div>
        <InlineText
          testId={`custom-field-${schema.field_id}`}
          value={value}
          placeholder="Agregá un valor…"
          onSave={onSave}
          className="text-sm text-gray-200"
        />
      </div>
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0 pt-0.5">
        {value && (
          <button
            data-testid={`custom-field-${schema.field_id}-clear`}
            onClick={onClear}
            className="text-gray-500 hover:text-amber-400 p-1"
            title="Borrar valor"
          ><Trash2 size={11} /></button>
        )}
        <button
          data-testid={`custom-field-${schema.field_id}-delete-schema`}
          onClick={onDeleteSchema}
          className="text-gray-500 hover:text-red-400 p-1"
          title="Eliminar campo global"
        ><Trash2 size={13} /></button>
      </div>
    </div>
  );
}

export function AddFieldButton({ onClick }) {
  return (
    <button
      data-testid="add-business-field-btn"
      onClick={onClick}
      className="flex items-center gap-1 text-xs text-gray-400 hover:text-[#FF4F00]"
    ><Plus size={11} /> Nuevo campo</button>
  );
}
